<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import lottie from "lottie-web";
import animationData from "@/assets/animations/agent-emotions.json";
import { AGENT_EMOTION_SEGMENTS } from "@/utils/agentEmotions";

const props = defineProps({
  emotion: { type: String, default: "greeting" }, // greeting | happy | listening | talking | thinking | sleep
  size: { type: [Number, String], default: "100%" },
});

const container = ref(null);
let anim = null;

// Bare numbers (26, "26") mean px; strings that already carry a unit
// (e.g. "100%", "2rem") pass through unchanged.
const sizeStyle = computed(() => {
  const s = props.size;
  return /^\d+$/.test(String(s)) ? `${s}px` : s;
});

const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function playEmotion(emotion) {
  const segment = AGENT_EMOTION_SEGMENTS[emotion] || AGENT_EMOTION_SEGMENTS.greeting;
  if (!anim) return;
  if (reduceMotion()) {
    anim.goToAndStop(segment[0], true);
  } else {
    anim.playSegments(segment, true);
  }
}

onMounted(() => {
  anim = lottie.loadAnimation({
    container: container.value,
    renderer: "svg",
    loop: true,
    autoplay: false,
    animationData,
  });
  anim.addEventListener("DOMLoaded", () => playEmotion(props.emotion));
});

watch(() => props.emotion, (emotion) => playEmotion(emotion));

onBeforeUnmount(() => {
  anim?.destroy();
});
</script>

<template>
  <div
    ref="container"
    class="agent-emotion"
    :style="{ width: sizeStyle, height: sizeStyle }"
    aria-hidden="true"
  ></div>
</template>

<style scoped>
.agent-emotion {
  display: inline-block;
  flex-shrink: 0;
  overflow: hidden;
}
</style>
